import React, { useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip, Stack } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import { useToast } from "../../services/utils";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteValidationScoringConfig,
  reactivateValidationScoringConfig,
} from "../../services/scoringService";
import { getAllResourceList } from "../../services/resourcesService";
import { getFormattedDateTime } from "../../services/utils";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  IconAudit,
  IconDeleteBlueSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconEyeBase,
  IconEditBase,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchPaginatedValidationScoringConfigs from "../../hooks/useFetchPaginatedValidationScoringConfigs";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ButtonComponent from "../../components/Button.Component";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { formatEntityLabel } from "../../utils/scoringFormatters";

const FILTER_CONFIG = {
  DEFAULT_STATE: { resource_id: "", status_filter: "Active" },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: ["resource_id"] as string[],
    STRING_FIELDS: ["status_filter"],
    DATE_FIELDS: [] as never[],
  },
} as const;

const ValidationScoringList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();
  const canEdit = isAdmin || hasPermission("validation_scoring_config", "edit");
  const canAdd = isAdmin || hasPermission("validation_scoring_config", "add");
  const canDelete =
    isAdmin || hasPermission("validation_scoring_config", "delete");
  const { rowsPerPage } = useRowsPerPage();
  const { t } = useAppTranslation();
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const parseUrlFilter = (field: string, raw: string): any => {
    if (FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.includes(field)) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : "";
    }
    return raw;
  };

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const s: any = { ...FILTER_CONFIG.DEFAULT_STATE };
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = parseUrlFilter(f, v);
    });
    return s;
  });

  const [resourceMap, setResourceMap] = useState<Map<number, string>>(
    new Map()
  );

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const resources = await getAllResourceList();
        if (Array.isArray(resources)) {
          const map = new Map<number, string>();
          resources.forEach((r: any) => {
            if (r.id != null) map.set(r.id, r.resource_name);
          });
          setResourceMap(map);
        }
      } catch {
        // silently fail — IDs will still be shown
      }
    };
    fetchResources();
  }, []);

  const [fetchedData, refetch] = useFetchPaginatedValidationScoringConfigs({
    setIsLoading,
    page,
    pSize,
    searchFilterData,
  });

  useEffect(() => {
    const p = new URLSearchParams();
    // Preserve ScoringConfig's reserved tab params so filter writes don't
    // wipe parent tab state from the URL.
    ["tab", "runSub", "aggSub"].forEach((k) => {
      const v = searchParams.get(k);
      if (v !== null) p.set(k, v);
    });
    FILTER_CONFIG.URL_FIELDS.forEach((f) => {
      const v = searchFilterData[f];
      if (v) p.set(f, String(v));
    });
    if (searchParams.toString() !== p.toString())
      setSearchParams(p, { replace: true });
  }, [searchFilterData, searchParams, setSearchParams]);

  useEffect(() => {
    const s: any = {};
    let changed = false;
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const urlVal = searchParams.get(f);
      if (urlVal) {
        s[f] = parseUrlFilter(f, urlVal);
      } else if (f === "status_filter") {
        s[f] = FILTER_CONFIG.DEFAULT_STATE.status_filter;
      } else {
        s[f] =
          searchFilterData[f] ??
          FILTER_CONFIG.DEFAULT_STATE[
            f as keyof typeof FILTER_CONFIG.DEFAULT_STATE
          ];
      }
    });
    changed = Object.keys(s).some((k) => s[k] !== searchFilterData[k]);
    if (changed) setSearchFilterData(s);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);
  useEffect(() => {
    if (fetchedData?.items) setFileData(fetchedData);
  }, [fetchedData]);

  const handleDelete = (row: any) => {
    setItemToDelete(row);
    setDeleteConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteValidationScoringConfig(itemToDelete.id);
      showToast(
        t("validationScoring.toast.deleted", {
          resourceId: itemToDelete.resource_id,
        }),
        "success"
      );
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      refetch();
    } catch {
      showToast(t("validationScoring.toast.failedDelete"), "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReactivate = async (row: any) => {
    setIsLoading(true);
    try {
      await reactivateValidationScoringConfig(row.id);
      showToast(
        t("validationScoring.toast.reactivated", { id: row.id }),
        "success"
      );
      refetch();
    } catch {
      showToast(t("validationScoring.toast.failedReactivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: () => <></>,
    },
    {
      field: "id",
      headerName: t("validationScoring.list.columns.id"),
      minWidth: 70,
      flex: 0.5,
      renderCell: (p: any) => <span>{p.id}</span>,
    },
    {
      field: "resource_id",
      headerName: t("validationScoring.list.columns.resource"),
      minWidth: 160,
      flex: 1.2,
      renderCell: (p: any) => {
        const id = p.value;
        const name = resourceMap.get(id);
        return (
          <LimitChractersWithTooltip value={formatEntityLabel(name, id)} />
        );
      },
    },
    {
      field: "impact_of_record_level",
      headerName: t("validationScoring.list.columns.recordImpact"),
      minWidth: 130,
      flex: 1,
      renderCell: (p: any) => <span>{p.value}</span>,
    },
    {
      field: "impact_of_issue_level",
      headerName: t("validationScoring.list.columns.issueImpact"),
      minWidth: 120,
      flex: 1,
      renderCell: (p: any) => <span>{p.value}</span>,
    },
    {
      field: "factor_low",
      headerName: t("validationScoring.list.columns.factorLow"),
      minWidth: 100,
      flex: 1,
      renderCell: (p: any) => <span>{p.value}</span>,
    },
    {
      field: "factor_medium",
      headerName: t("validationScoring.list.columns.factorMed"),
      minWidth: 100,
      flex: 1,
      renderCell: (p: any) => <span>{p.value}</span>,
    },
    {
      field: "factor_high",
      headerName: t("validationScoring.list.columns.factorHigh"),
      minWidth: 100,
      flex: 1,
      renderCell: (p: any) => <span>{p.value}</span>,
    },
    {
      field: "modified_by_user",
      headerName: t("validationScoring.list.columns.modifiedBy"),
      minWidth: 130,
      flex: 1,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={p?.value?.username} />
      ),
    },
    {
      field: "updated_on",
      headerName: t("validationScoring.list.columns.updatedOn"),
      minWidth: 160,
      flex: 1,
      renderCell: (p: any) => getFormattedDateTime(p.value),
    },
    {
      field: "is_active",
      headerName: t("validationScoring.list.columns.status"),
      minWidth: 80,
      flex: 0.7,
      renderCell: (p: any) =>
        p.value ? (
          <Box title={t("validationScoring.list.tooltip.active")}>
            <IconCheckCircleIconGreenSvg width={21} height={21} />
          </Box>
        ) : (
          <Box title={t("validationScoring.list.tooltip.inactive")}>
            <IconCrossCircleIconSvg width={21} height={21} />
          </Box>
        ),
    },
    {
      field: "action",
      headerName: t("validationScoring.list.columns.action"),
      align: "right",
      headerAlign: "right",
      width: 140,
      renderCell: (p: any) => {
        const isActive = p?.row?.is_active === true;
        return (
          <>
            {isActive && (
              <>
                <Tooltip
                  title={t("validationScoring.list.tooltip.view")}
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      navigate(`/scoring/validation/view/${p.row.id}`)
                    }
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip
                    title={t("validationScoring.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(`/scoring/validation/edit/${p.row.id}`)
                      }
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    <IconButton
                      key="audit"
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(
                          `/scoring/validation/audit/${p.row.resource_id}`,
                          { state: { configId: p.row.id } }
                        )
                      }
                    >
                      <IconAudit /> {t("validationScoring.list.dropdown.audit")}
                    </IconButton>,
                    ...(canDelete
                      ? [
                          <IconButton
                            key="delete"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete(p.row)}
                          >
                            <IconDeleteBlueSvg />{" "}
                            {t("validationScoring.list.dropdown.delete")}
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}
            {!isActive && canEdit && (
              <Tooltip
                title={t("validationScoring.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(p.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      {(isLoading || deleteLoading) && (
        <Loader isLoading={isLoading || deleteLoading} />
      )}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("validationScoring.list.dialog.deleteTitle")}
        dailogDescription={t(
          "validationScoring.list.dialog.deleteDescription",
          {
            id: itemToDelete?.id,
          }
        )}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["resource_list", "status_filter"]}
              primaryFields={["resource_list", "status_filter"]}
              fieldRequirements={{
                queryNamePlaceholder: t("validationScoring.list.placeholder"),
                defaultStatusFilter: "Active",
                includeResourceId: true,
              }}
            />
          </Grid>
          {canAdd && (
            <Grid item display={"flex"} justifyContent={"flex-end"}>
              <Stack>
                <Box sx={{ display: "flex", columnGap: 2 }}>
                  <ButtonComponent
                    handelClickEvent={() => navigate("/scoring/validation/add")}
                    className="btn-orange"
                  >
                    <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                    {t("validationScoring.list.addButton")}
                  </ButtonComponent>
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={t("validationScoring.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default ValidationScoringList;
